/** World and level identifiers (GDD §6.4, §10). */
export type WorldId = 1 | 2 | 3 | 4;
export type LevelNumber = 1 | 2 | 3;

/** "1-1" ... "4-3" in the save file; shown as OP-101 ... OP-403. */
export type LevelId = `${WorldId}-${LevelNumber}`;

export const WORLD_IDS: readonly WorldId[] = [1, 2, 3, 4];

export const LEVEL_IDS: readonly LevelId[] = WORLD_IDS.flatMap((world) =>
  ([1, 2, 3] as const).map((level): LevelId => `${world}-${level}`),
);

export function isLevelId(value: string): value is LevelId {
  return (LEVEL_IDS as readonly string[]).includes(value);
}

export function parseLevelId(id: string): { world: WorldId; level: LevelNumber } {
  if (!isLevelId(id)) throw new Error(`Unknown level "${id}"`);
  const [world, level] = id.split('-').map(Number);
  return { world: world as WorldId, level: level as LevelNumber };
}

/** "1-2" to "OP-102"; the test level "0-0" is "OP-000". */
export function opFor(id: LevelId | TestLevelId): string {
  if (isTestLevelId(id)) return 'OP-000';
  const { world, level } = parseLevelId(id);
  return `OP-${world}0${level}`;
}

/** The test level OP-000 (GDD §16.4): outside the campaign, never saved and never listed as a mission. */
export type TestLevelId = '0-0';
export const TEST_LEVEL_ID: TestLevelId = '0-0';

export function isTestLevelId(value: string): value is TestLevelId {
  return value === TEST_LEVEL_ID;
}
